import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
export function SocialProof() {
  const stats = [{
    value: '+300',
    label: 'Profes'
  }, {
    value: '4.9',
    label: 'Rating'
  }, {
    value: '14',
    label: 'Materiales'
  }, {
    value: '660+',
    label: 'Páginas'
  }];
  const testimonials = [{
    quote: 'Armé toda la planificación de mi temporada en un fin de semana. Ahora llego a cada práctica sabiendo exactamente qué trabajar. Mis pibes progresan de verdad por primera vez.',
    name: 'Martín G.',
    role: 'DT de Cadetes en Club de Barrio',
    location: 'Buenos Aires',
    initials: 'MG',
    color: 'bg-blue-600'
  }, {
    quote: 'Las fichas de seguimiento cambiaron todo. Cuando un padre me pregunta por su hijo, tengo la respuesta en la mano. No más situaciones incómodas ni respuestas improvisadas.',
    name: 'Rodrigo P.',
    role: 'Profe de Escuelita de Básquet',
    location: 'Rosario',
    initials: 'RP',
    color: 'bg-brand-orange'
  }, {
    quote: 'Empecé a usar las planillas de práctica y en 3 semanas el nivel del equipo subió notoriamente. Los pibes lo notan, los padres lo ven. Vale cada peso.',
    name: 'Luciana M.',
    role: 'Entrenadora de Infantiles y Mini',
    location: 'Córdoba',
    initials: 'LM',
    color: 'bg-purple-600'
  }, {
    quote: 'Soy voluntario y entreno después del laburo. Con este sistema bajé un 70% el tiempo de preparación y los pibes notaron la diferencia desde la segunda semana. Ahora me piden no faltar a la práctica.',
    name: 'Joaquín D.',
    role: 'Profe de Mini Básquet en Club Federado',
    location: 'Mendoza',
    initials: 'JD',
    color: 'bg-emerald-600'
  }, {
    quote: 'Coordino cuatro categorías y antes era un caos. Hoy tengo a todos mis profes alineados con el mismo sistema y los resultados se ven en la cancha. Lo recomiendo con los ojos cerrados.',
    name: 'Sebastián R.',
    role: 'Coordinador de Inferiores',
    location: 'La Plata',
    initials: 'SR',
    color: 'bg-red-600'
  }];
  const [active, setActive] = useState(0);
  const [direction, setDirection] = useState(0);
  const total = testimonials.length;
  const autoplayRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const goTo = (idx: number) => {
    setDirection(idx > active ? 1 : -1);
    setActive((idx + total) % total);
  };
  const next = () => goTo(active + 1);
  const prev = () => goTo(active - 1);
  // Autoplay (pauses on hover/touch via the ref reset)
  useEffect(() => {
    autoplayRef.current = setInterval(() => {
      setDirection(1);
      setActive(a => (a + 1) % total);
    }, 6000);
    return () => {
      if (autoplayRef.current) clearInterval(autoplayRef.current);
    };
  }, [total]);
  const pauseAutoplay = () => {
    if (autoplayRef.current) clearInterval(autoplayRef.current);
  };
  // Swipe handling
  const touchStartX = useRef<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    pauseAutoplay();
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) {
      if (dx < 0) next();else prev();
    }
    touchStartX.current = null;
  };
  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 60 : -60,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -60 : 60,
      opacity: 0
    })
  };
  const t = testimonials[active];
  return <section className="bg-brand-dark text-white py-12 md:py-20 relative overflow-hidden" data-id="element-308">
      {/* Stats Bar */}
      <div className="max-w-6xl mx-auto px-4 mb-12 md:mb-20" data-id="element-309">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-6 md:gap-8 md:divide-x md:divide-gray-800" data-id="element-310">
          {stats.map((stat, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.08
        }} className="text-center px-2 md:px-4" data-id="element-311">
              <div className="font-display text-4xl md:text-7xl text-brand-orange leading-none mb-1 md:mb-2" data-id="element-312">
                {stat.value}
              </div>
              <div className="font-bold text-gray-400 uppercase tracking-widest text-[10px] md:text-sm" data-id="element-313">
                {stat.label}
              </div>
            </motion.div>)}
        </div>
      </div>

      {/* Testimonials Slider */}
      <div className="max-w-3xl mx-auto px-4" data-id="element-314">
        <div className="text-center mb-8 md:mb-10" data-id="element-315">
          <h3 className="text-brand-orange font-bold tracking-widest uppercase text-xs md:text-sm mb-2" data-id="element-316">
            Lo que dicen los profes
          </h3>
          <h2 className="font-display text-3xl md:text-5xl leading-tight" data-id="element-317">
            RESULTADOS REALES EN LA CANCHA
          </h2>
        </div>

        <div className="relative" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} onMouseEnter={pauseAutoplay} data-id="element-318">
          {/* Arrow Buttons (desktop only) */}
          <button onClick={prev} aria-label="Anterior testimonio" className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-12 w-10 h-10 rounded-full bg-gray-900 hover:bg-brand-orange items-center justify-center transition-colors z-10 border border-gray-800" data-id="element-319">
            <ChevronLeft size={20} data-id="element-320" />
          </button>
          <button onClick={next} aria-label="Siguiente testimonio" className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-12 w-10 h-10 rounded-full bg-gray-900 hover:bg-brand-orange items-center justify-center transition-colors z-10 border border-gray-800" data-id="element-321">
            <ChevronRight size={20} data-id="element-322" />
          </button>

          {/* Slide */}
          <div className="relative min-h-[320px] md:min-h-[280px] overflow-hidden" data-id="element-323">
            <AnimatePresence custom={direction} mode="wait" data-id="element-324">
              <motion.div key={active} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.35,
              ease: 'easeOut'
            }} className="bg-gray-900 p-6 md:p-10 rounded-2xl border border-gray-800 relative shadow-2xl" data-id="element-325">
                <Quote className="absolute top-4 right-4 md:top-6 md:right-6 text-brand-orange/20" size={48} data-id="element-326" />

                <div className="flex mb-4 text-brand-orange" data-id="element-327">
                  {[...Array(5)].map((_, j) => <Star key={j} size={16} fill="currentColor" data-id="element-328" />)}
                </div>
                <p className="text-gray-200 text-base md:text-xl italic mb-6 md:mb-8 font-medium leading-relaxed relative z-10" data-id="element-329">
                  "{t.quote}"
                </p>
                <div className="flex items-center pt-4 md:pt-6 border-t border-gray-800" data-id="element-330">
                  <div className={`w-12 h-12 md:w-14 md:h-14 rounded-full ${t.color} flex items-center justify-center font-display text-lg md:text-xl mr-4 shrink-0`} data-id="element-331">
                    {t.initials}
                  </div>
                  <div data-id="element-332">
                    <div className="font-bold text-white text-sm md:text-base" data-id="element-333">
                      {t.name}
                    </div>
                    <div className="text-xs md:text-sm text-gray-400" data-id="element-334">
                      {t.location} — {t.role}
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-6" data-id="element-335">
            {testimonials.map((_, i) => <button key={i} onClick={() => {
            pauseAutoplay();
            goTo(i);
          }} aria-label={`Ir al testimonio ${i + 1}`} className={`transition-all duration-300 rounded-full ${i === active ? 'bg-brand-orange w-8 h-2' : 'bg-gray-700 hover:bg-gray-600 w-2 h-2'}`} data-id="element-336" />)}
          </div>

          {/* Mobile swipe hint */}
          <p className="md:hidden text-center text-xs text-gray-500 mt-4 font-medium" data-id="element-337">
            ← Deslizá para ver más opiniones →
          </p>
        </div>
      </div>
    </section>;
}